import React, { useState, useEffect } from "react";
import { useDateFormatter, usePageTitle, useScrollToTop } from "../hooks";
import { MainCard } from "../components/commons";
import articleHero from "../assets/herobg/article-hero.webp";
import { SearchOutlined } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import arrowIcon from "../assets/icons/arrow.png";
import { getAllArticlePublished } from "../services/article.service";
import { searchArticle } from "../services/search.service"; // <-- Utilisation du service

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const Search = () => {
    usePageTitle("Recherche");
    useScrollToTop();
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        total: 0,
        lastPage: 1,
        perPage: 10
    });
    const [hasSearched, setHasSearched] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const formatDate = useDateFormatter();

    const formatRubriques = (rubriques) => {
        if (!rubriques || !Array.isArray(rubriques) || rubriques.length === 0) {
            return '';
        }
        return rubriques.map(rubrique => rubrique.name_rub).join(', ');
    };

    // Récupère le mot-clé de l'URL et effectue la recherche automatiquement
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const keyword = params.get("keyword");
        if (keyword) {
            setSearchQuery(keyword);
            setHasSearched(true);
            setPage(1);
            handleSearchFromKeyword(keyword);
        } else {
            setSearchQuery("");
            setHasSearched(false);
            setPage(1);
            fetchInitialArticles(1, pagination.perPage);
        }
        // eslint-disable-next-line
    }, [location.search]);

    // Fonction de recherche par mot-clé
    const handleSearchFromKeyword = async (keyword) => {
        setLoading(true);
        try {
            const result = await searchArticle({ q: keyword });
            setArticles(result.hits || []);
            setPagination({
                total: result.hits?.length || 0,
                lastPage: 1,
                perPage: 10
            });
        } catch (error) {
            setArticles([]);
            setPagination({
                total: 0,
                lastPage: 1,
                perPage: 10
            });
            console.error("[Search] Erreur lors de la recherche par mot-clé :", error);
        } finally {
            setLoading(false);
        }
    };

    // Fonction recherche classique (champ de saisie)
    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setHasSearched(true);
        setPage(1);
        navigate(`/recherche?keyword=${encodeURIComponent(searchQuery)}`);
    };

    // Chargement initial des articles si pas de recherche
    const fetchInitialArticles = async (currentPage, perPage) => {
        setLoading(true);
        try {
            const result = await getAllArticlePublished(currentPage, perPage);
            setArticles(prev => {
                if (currentPage === 1) return result.data;
                return [...prev, ...result.data];
            });
            setPagination({
                total: result.meta.total,
                lastPage: result.meta.last_page,
                perPage: result.meta.per_page
            });
        } catch (error) {
            setArticles([]);
            setPagination({
                total: 0,
                lastPage: 1,
                perPage: 10
            });
            console.error("[Search] Erreur lors du chargement des articles :", error);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreArticles = async () => {
        if (page >= pagination.lastPage) return;
        const nextPage = page + 1;
        setPage(nextPage);
        await fetchInitialArticles(nextPage, pagination.perPage);
    };

    const handleArticleClick = (slug) => {
        navigate(`/article/${slug}`);
    };

    const resetSearch = () => {
        navigate("/recherche");
    };

    // Affichage : dans le cas d'une recherche, ce sont les résultats, sinon c'est la liste classique paginée
    const displayedArticles = articles;

    return (
        <div className="min-h-screen flex flex-col">
            <div className="flex justify-center px-4 sm:px-6 my-8">
                <form onSubmit={handleSearch} className="flex max-w-6xl mx-auto w-full">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Titres, Mots-clés, Auteurs"
                        className="flex-grow p-2.5 text-gray-800 bg-inputbackground border border-greenlogo focus:outline-none focus:ring-greenlogo text-base"
                    />
                    <button
                        type="submit"
                        className="px-5 text-black bg-greenlogo font-light flex items-center gap-2"
                    >
                        <SearchOutlined /> Recherche
                    </button>
                </form>
            </div>

            {hasSearched && (
                <div className="flex justify-between items-center px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto w-full mb-4">
                    <p className="text-sm text-gray-600">
                        Résultats pour "{searchQuery}" ({displayedArticles.length})
                    </p>
                    <button
                        onClick={resetSearch}
                        className="text-sm text-greenlogo hover:underline"
                    >
                        Afficher tous les articles
                    </button>
                </div>
            )}

            <div className="flex-grow px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto w-full mb-12">
                {loading && (
                    <div className="text-center py-6">
                        <div className="inline-block w-8 h-8 border-4 border-greenlogo border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                {!loading && displayedArticles.length === 0 && (
                    <div className="text-center py-6">
                        {hasSearched
                            ? `Aucun résultat trouvé pour "${searchQuery}"`
                            : "Aucun article disponible"
                        }
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayedArticles.map((article) => (
                        <div
                            key={article.id_art}
                            className="cursor-pointer"
                            onClick={() => handleArticleClick(article.slug)}
                        >
                            <MainCard
                                id={article.id_art}
                                cover={article.photo_art ? `${REACT_APP_BASE_URL}/${article.photo_art}` : articleHero}
                                rubrique={article.rubriques && Array.isArray(article.rubriques)
                                    ? formatRubriques(article.rubriques)
                                    : article.name_rub}
                                title={article.title_art?.fr}
                                description={article.key_words_art?.fr?.join(", ")}
                                date={formatDate(article.date_pub_art)}
                                auteur={`${article.author?.title_aut || ""} ${article.author?.first_name || ""} ${article.author?.last_name || ""}`}
                            />
                        </div>
                    ))}
                </div>

                {!loading && !hasSearched && pagination.total > articles.length && page < pagination.lastPage && (
                    <div className="flex justify-center mt-6">
                        <button
                            onClick={loadMoreArticles}
                            className="w-fit inline-flex items-center gap-2 px-6 py-3 bg-greenlogo font-semibold text-white"
                        >
                            Voir plus d'articles
                            <img src={arrowIcon} className="ml-2 sm:ml-3 h-3 w-auto sm:h-4" alt="Voir plus" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Search;